// M004-F0747 design section 3.3 / 8: the submit-claim review - a modal-less panel over the grid (Esc cancels). It is built from the
// Ledger's DRY-RUN read (envelope.claim.submit with dry_run: nothing changes): receipts, total, VAT and the issues that BLOCK submit
// (each with Show them, which filters the grid). "Claim to" lists the organisations I belong to with a Ledger (the envelope's own
// preselected; one on another Ledger installation is disabled with the reason). Submit is disabled while any issue stands, while the
// dry-run is loading or has failed, and while a submit is in flight. After Submit the envelope locks (the parent applies the answer).
// The claimant sees no approve / return control anywhere in here.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { envelopeApi } from '../../services/envelopeApi';
import { extractEnvelopeError } from './envelopeModel';
import { claimOrgOptions, issueIds, issueSentence, summaryLine } from './claimModel';
import { confirmedAmountText, formatShortDate, receiptDate, receiptHeadline } from './envelopeWorkspaceModel';

const unwrap = (response) => response?.data?.data || {};

export default function ClaimReviewPanel({ orgId, envelope, receipts, organisations, resubmit = false, offline = false, onCancel, onShowIssue, onSubmitted }) {
  const [dry, setDry] = useState({ status: 'loading', summary: null, issues: [], error: null });
  const [target, setTarget] = useState(String(orgId));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const headingRef = useRef(null);
  const seq = useRef(0);
  const options = claimOrgOptions(organisations, orgId);
  const label = envelope.name;
  const verb = resubmit ? 'Resubmit' : 'Submit';

  const read = useCallback(async () => {
    seq.current += 1;
    const mine = seq.current;
    setDry((current) => ({ ...current, status: 'loading', error: null }));
    try {
      const data = unwrap(await envelopeApi.claimSubmit(orgId, envelope.envelope_id, { dryRun: true }));
      if (mine !== seq.current) return;
      setDry({ status: 'ready', summary: data.summary || null, issues: Array.isArray(data.issues) ? data.issues : [], error: null });
    } catch (cause) {
      if (mine !== seq.current) return;
      setDry({ status: 'error', summary: null, issues: [], error: extractEnvelopeError(cause) });
    }
  }, [orgId, envelope.envelope_id]);

  // Re-read when the receipts change (the claimant fixed an amount the panel named): the issues are always the Ledger's current answer.
  const signature = receipts.map((r) => `${r.receipt_document_id}:${r.amount?.minor ?? ''}:${r.ocr?.status ?? ''}`).join('|');
  useEffect(() => { read(); }, [read, signature]);
  useEffect(() => { if (headingRef.current) headingRef.current.focus(); }, []);

  const blocked = dry.status !== 'ready' || dry.issues.length > 0 || submitting || offline;

  const submit = async () => {
    if (blocked) return;
    setSubmitting(true);
    setError(null);
    try {
      const view = unwrap(await envelopeApi.claimSubmit(orgId, envelope.envelope_id, { targetOrgId: target }));
      onSubmitted(view, target);
    } catch (cause) {
      setError(extractEnvelopeError(cause));
      setSubmitting(false);
      read(); // the Ledger's own answer may mean the envelope changed meanwhile: re-read what blocks
    }
  };

  const headline = `${verb} "${label}" as an expense claim`;
  return (
    <section
      className="ew-claim-panel"
      aria-labelledby="ew-claim-title"
      data-testid="claim-review"
      data-state={dry.status}
      onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); onCancel(); } }}
    >
      <h3 id="ew-claim-title" tabIndex={-1} ref={headingRef} data-testid="claim-review-title">{headline}</h3>

      {dry.status === 'loading' ? <p className="ew-muted" role="status" data-testid="claim-review-loading">Checking the envelope with the Ledger…</p> : null}
      {dry.status === 'error' ? (
        <div className="ew-error" role="alert" data-testid="claim-review-error">
          <p>{dry.error.message}</p>
          {dry.error.code ? <p>Error code: <code data-testid="claim-review-error-code">{dry.error.code}</code></p> : null}
          <button type="button" className="ew-btn" data-testid="claim-review-retry" onClick={read}>Try again</button>
        </div>
      ) : null}

      {dry.status === 'ready' ? (
        <>
          <p className="ew-claim-summary" data-testid="claim-review-summary"><strong>{summaryLine(dry.summary)}</strong></p>
          <ul className="ew-claim-rows" data-testid="claim-review-rows" aria-label="Receipts in this claim" tabIndex={0}>
            {receipts.map((receipt) => (
              <li key={receipt.receipt_document_id} data-testid={`claim-review-row-${receipt.receipt_document_id}`}>
                <span className="ew-claim-row-date">{formatShortDate(receiptDate(receipt)) || '—'}</span>
                <span className="ew-claim-row-name">{receiptHeadline(receipt).text}</span>
                <span className="ew-claim-row-amount">{confirmedAmountText(receipt) || 'no amount'}</span>
              </li>
            ))}
          </ul>
          {dry.issues.length ? (
            <ul className="ew-claim-issues" data-testid="claim-review-issues" aria-label="Blocking: fix these before submitting">
              {dry.issues.map((issue) => (
                <li key={issue.code} data-testid={`claim-review-issue-${issue.code}`}>
                  <span><strong>Blocking:</strong> {issueSentence(issue)}</span>
                  {issueIds(issue).length ? (
                    <button type="button" className="ew-link" data-testid={`claim-review-show-${issue.code}`} onClick={() => onShowIssue(issueIds(issue))}>Show them</button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}

      <fieldset className="ew-claim-orgs" data-testid="claim-review-orgs">
        <legend>Claim to</legend>
        {options.map((option) => (
          <label key={option.id} className={`ew-claim-org${option.disabled ? ' ew-claim-org-disabled' : ''}`}>
            <input
              type="radio"
              name="ew-claim-org"
              value={option.id}
              checked={target === option.id}
              disabled={option.disabled || submitting}
              aria-describedby={option.disabled ? `ew-claim-org-why-${option.id}` : undefined}
              data-testid={`claim-review-org-${option.id}`}
              onChange={() => setTarget(option.id)}
            />
            <span>{option.label}</span>
            {option.disabled ? <span id={`ew-claim-org-why-${option.id}`} className="ew-muted" data-testid={`claim-review-org-why-${option.id}`}>{option.reason}</span> : null}
          </label>
        ))}
      </fieldset>

      <p className="ew-muted" data-testid="claim-review-note">After you {verb.toLowerCase()}, the envelope locks. A reviewer approves or returns the whole envelope.</p>
      {error ? (
        <div className="ew-error" role="alert" data-testid="claim-review-submit-error">
          <p>{error.message}</p>
          {error.code ? <p>Error code: <code data-testid="claim-review-submit-error-code">{error.code}</code></p> : null}
        </div>
      ) : null}
      <div className="ew-claim-actions">
        <button type="button" className="ew-btn" data-testid="claim-review-cancel" onClick={onCancel}>Cancel</button>
        <button type="button" className="ew-btn ew-btn-primary" data-testid="claim-review-submit" disabled={blocked} onClick={submit}>
          {submitting ? `${verb === 'Submit' ? 'Submitting' : 'Resubmitting'}…` : `${verb} claim`}
        </button>
      </div>
    </section>
  );
}
